import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '../../../components/Layout';
import { useI18n } from '../../../hooks/useI18n';
import { apiFetch } from '../../../utils/api';
import { formatCurrency } from '../../../utils/formatters';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import PaginationFooter from '../../../components/PaginationFooter';
import {
    Search,
    Plus,
    Eye,
    Edit
} from 'lucide-react';

interface MenuItem {
    id: string;
    name: string;
    description?: string;
    base_price?: number;
    base_cost_price?: number;
    is_active: boolean;
    fruit_master_id?: string;
    fruit_master_name?: string;
    category_id?: string;
    created_at: string;
    updated_at?: string;
}

interface MenuItemsResponse {
    items: MenuItem[];
    total: number;
    page: number;
    per_page: number;
}

interface Category {
    id: string;
    name: string;
}

interface Filters {
    status: string;
    search: string;
    category_id: string;
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

const statusOptions = [
    { value: 'all', label: 'All Items' },
    { value: 'true', label: 'Active Only' },
    { value: 'false', label: 'Inactive Only' },
];

export default function MenusPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();

    // State
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFilters] = useState<Filters>({
        status: 'all',
        search: '',
        category_id: 'all',
    });

    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 50,
    });

    // Fetch menu items
    const fetchMenuItems = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                per_page: pagination.limit.toString(),
            });

            if (filters.status && filters.status !== 'all') {
                params.append('is_active', filters.status);
            }

            if (filters.category_id && filters.category_id !== 'all') {
                params.append('category_id', filters.category_id);
            }

            if (filters.search) {
                params.append('search', filters.search);
            }

            const response = await apiFetch(`/api/menu-items?${params.toString()}`);

            if (response.ok) {
                const data: MenuItemsResponse = await response.json();
                setMenuItems(data.items || []);
                setPagination(prev => ({
                    ...prev,
                    total: data.total || 0,
                    pages: Math.ceil((data.total || 0) / pagination.limit),
                }));
            } else {
                console.error('API response not ok:', response.status, response.statusText);
                setMenuItems([]);
            }

        } catch (err) {
            console.error('Error fetching menu items:', err);
            setError(err instanceof Error ? err.message : 'Failed to load menu items');
        } finally {
            setLoading(false);
        }
    };

    // Fetch categories
    const fetchCategories = async () => {
        try {
            const response = await apiFetch('/api/categories');
            if (response.ok) {
                const data = await response.json();
                setCategories(data.categories || []);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    // Effects
    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchMenuItems();
    }, [pagination.page, filters]);

    // Handlers
    const handleFilterChange = (key: keyof Filters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    };

    const handlePageChange = (newPage: number) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const clearFilters = () => {
        setFilters({
            status: 'all',
            search: '',
            category_id: 'all',
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleCreateNew = () => {
        navigate({ to: '/menus/new'});
    };

    const handleEdit = (menuItem: MenuItem) => {
        navigate({ to: `/menus/${menuItem.id}/edit}`});
    };

    const handleView = (menuItem: MenuItem) => {
        navigate({ to: `/menus/${menuItem.id}`});
    };

    const hasActiveFilters = filters.status !== 'all' || filters.search || filters.category_id !== 'all';

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <h1 className="text-3xl font-bold tracking-tight">
                        {t('navigation.menuItems', 'common')}
                    </h1>
                    <Button onClick={handleCreateNew} className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Menu Item
                    </Button>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center space-x-2">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search menu items..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            className="w-64"
                        />
                    </div>

                    <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                        <SelectTrigger className="w-48">
                            <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                            {statusOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select value={filters.category_id} onValueChange={(value) => handleFilterChange('category_id', value)}>
                        <SelectTrigger className="w-48">
                            <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Categories</SelectItem>
                            {categories.map((category) => (
                                <SelectItem key={category.id} value={category.id}>
                                    {category.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Button variant="outline" onClick={clearFilters}>
                        Clear Filters
                    </Button>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                            Filters are active
                        </span>
                        <Button variant="outline" onClick={clearFilters}>
                            Clear All Filters
                        </Button>
                    </div>
                )}

                {/* Menu Items Table */}
                <div className="">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Base Price</TableHead>
                                <TableHead>Cost Price</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Fruit ID</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                    </TableRow>
                                ))
                            ) : error ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-red-600">
                                        <p>Error: {error}</p>
                                        <Button
                                            variant="outline"
                                            onClick={fetchMenuItems}
                                            className="mt-2"
                                        >
                                            Retry
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ) : menuItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                        No menu items found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                menuItems.map((item) => (
                                    <TableRow
                                        key={item.id}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => handleView(item)}
                                    >
                                        <TableCell className="font-medium">
                                            {item.name}
                                        </TableCell>
                                        <TableCell className="max-w-xs truncate">
                                            {item.description || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {item.base_price ? formatCurrency(item.base_price) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {item.base_cost_price ? formatCurrency(item.base_cost_price) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={item.is_active ? "default" : "secondary"}>
                                                {item.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">
                                            {item.fruit_master_id || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleView(item);
                                                    }}
                                                    className="h-8 w-8 p-0"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(item);
                                                    }}
                                                    className="h-8 w-8 p-0"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Pagination */}
                <PaginationFooter
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onPageSizeChange={(pageSize) => {
                        setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
                    }}
                />
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/menus/')({
  component: MenusPage,
})
